import React, { useMemo } from "react";
import { Lock, Star as StarIcon } from "@mui/icons-material";
import {
    Box,
    Button,
    Chip,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { DIFF_LEVELS } from "../../../admin/story/adminSlidingUi";
import { usePagesTranslation } from "../../locales";
import { isSlidingTemplateCompletedByUser } from "../../browse-sliding/slidingBrowseUtils";
import { getSlidingGridLockReason, isSlidingPremiumTier } from "../common/puzzleTierAccess";
import { saveSlidingPrefsFromTemplate } from "../common/puzzlePlayPrefs";
import { JIGSAW_PLAY_WARM } from "../jigsaw/TemplateLevelGroup";

const playContainedButtonSx = {
    background: `linear-gradient(165deg, #E0A85A 0%, ${JIGSAW_PLAY_WARM.main} 48%, ${JIGSAW_PLAY_WARM.dark} 100%)`,
    color: "#1a1208",
    fontWeight: 700,
    letterSpacing: "0.04em",
    boxShadow: `0 4px 14px ${JIGSAW_PLAY_WARM.soft}, inset 0 1px 0 rgba(255,255,255,0.28)`,
    transition: "transform 160ms ease, box-shadow 160ms ease, filter 160ms ease",
    "&:hover": {
        background: `linear-gradient(165deg, #E8B56A 0%, #D49644 48%, ${JIGSAW_PLAY_WARM.dark} 100%)`,
        boxShadow: `0 8px 22px ${JIGSAW_PLAY_WARM.glow}, inset 0 1px 0 rgba(255,255,255,0.35)`,
        filter: "brightness(1.04)",
        transform: "translateY(-1px)",
    },
    "&:active": {
        transform: "translateY(0)",
        boxShadow: `0 2px 10px ${JIGSAW_PLAY_WARM.soft}`,
    },
    "&.Mui-disabled": {
        background: "rgba(148, 163, 184, 0.2)",
        color: "rgba(226, 232, 240, 0.45)",
        boxShadow: "none",
        filter: "none",
        transform: "none",
    },
};

const LABEL_PRIMARY = "#eef4ff";
const LABEL_MUTED = "rgba(226, 232, 240, 0.78)";

const SECTION_LABEL_SX = {
    fontWeight: 700,
    fontSize: "0.95rem",
    mb: 1,
    color: LABEL_MUTED,
};

const LEVEL_COLORS = {
    EASY: "success",
    MEDIUM: "warning",
    HARD: "error",
};

const tierBadgeChipSx = {
    height: 22,
    "& .MuiChip-label": { px: 0.65, fontSize: "0.68rem", fontWeight: 600 },
    "& .MuiChip-icon": { fontSize: "0.8rem", marginLeft: "4px", marginRight: "-2px" },
};

function GridSizeOption({ label, selected, disabled, onClick }) {
    return (
        <Button
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-pressed={selected}
            sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 44,
                px: 2,
                py: 0.85,
                borderRadius: 999,
                border: 1,
                borderColor: selected ? "rgba(224, 168, 90, 0.85)" : "rgba(148, 163, 184, 0.35)",
                bgcolor: selected ? "rgba(201, 137, 58, 0.28)" : "rgba(255,255,255,0.06)",
                color: selected ? LABEL_PRIMARY : LABEL_MUTED,
                fontWeight: selected ? 700 : 500,
                fontSize: "0.95rem",
                textTransform: "none",
                letterSpacing: "0.02em",
                "&:hover": {
                    bgcolor: selected ? "rgba(201, 137, 58, 0.36)" : "rgba(255,255,255,0.12)",
                },
                "&.Mui-disabled": {
                    bgcolor: "rgba(148, 163, 184, 0.08)",
                    color: "rgba(148, 163, 184, 0.45)",
                    borderColor: "rgba(148, 163, 184, 0.2)",
                },
            }}
        >
            {`[ ${label} ]`}
        </Button>
    );
}

function findEntry(entries, gridSize, level) {
    return (
        entries.find(
            (e) => Number(e.gridSize) === Number(gridSize) && e.level === level && e.template,
        ) ?? null
    );
}

function firstEntryForGrid(entries, gridSize) {
    for (const level of DIFF_LEVELS) {
        const entry = findEntry(entries, gridSize, level);
        if (entry) return entry;
    }
    return null;
}

/**
 * Grid size + difficulty picker for the play dialog.
 * Choosing options only selects; Play navigates to the board.
 */
export default function SlidingLevelGroup({
    levelEntries,
    selectedTemplate,
    setSelectedTemplate,
    accessContext,
    cloudUserId,
    onUpgrade,
    navigationState,
    isFreePuzzle = false,
}) {
    const { t } = usePagesTranslation();
    const navigate = useNavigate();
    const { storyId } = useParams();
    const list = levelEntries ?? [];

    const gridSizes = useMemo(() => {
        const sizes = new Set(list.map((e) => Number(e.gridSize)).filter((n) => n > 0));
        return [...sizes].sort((a, b) => a - b);
    }, [list]);

    if (list.length === 0) return null;

    const selectedGridSize = selectedTemplate ? Number(selectedTemplate.gridSize) : 0;
    const selectedLevel = String(selectedTemplate?.difficultyLevel ?? "").toUpperCase();
    const lockReason = selectedTemplate
        ? getSlidingGridLockReason(selectedGridSize, accessContext, selectedLevel, { isFreePuzzle })
        : null;

    const handleGridSelect = (gridSize) => {
        const entry =
            findEntry(list, gridSize, selectedLevel) ?? firstEntryForGrid(list, gridSize);
        if (entry?.template) setSelectedTemplate(entry.template);
    };

    const handleLevelSelect = (level) => {
        const entry = findEntry(list, selectedGridSize, level);
        if (entry?.template) {
            setSelectedTemplate(entry.template);
            return;
        }
        for (const gs of gridSizes) {
            const alt = findEntry(list, gs, level);
            if (alt?.template) {
                setSelectedTemplate(alt.template);
                return;
            }
        }
    };

    const handlePlay = () => {
        if (!selectedTemplate?.id || !storyId) return;
        if (lockReason === "upgrade") {
            onUpgrade?.();
            return;
        }
        if (lockReason === "login") {
            navigate("/login", {
                state: { from: { pathname: `/puzzle/play-sliding/${storyId}` } },
            });
            return;
        }
        if (lockReason) return;
        if (cloudUserId) {
            saveSlidingPrefsFromTemplate(cloudUserId, selectedTemplate);
        }
        navigate(`/puzzle/play-sliding/${storyId}/board/${selectedTemplate.id}`, {
            state: navigationState,
        });
    };

    return (
        <Stack spacing={2.5}>
            <Box>
                <Typography component="h3" sx={SECTION_LABEL_SX}>
                    {t("puzzle.sliding.select-grid-size")}
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                    {gridSizes.map((gs) => {
                        const hasTemplate = list.some(
                            (e) => Number(e.gridSize) === gs && e.template,
                        );
                        return (
                            <GridSizeOption
                                key={gs}
                                label={`${gs} × ${gs}`}
                                selected={selectedGridSize === gs}
                                disabled={!hasTemplate}
                                onClick={() => handleGridSelect(gs)}
                            />
                        );
                    })}
                </Box>
            </Box>

            <Box>
                <Typography component="h3" sx={SECTION_LABEL_SX}>
                    {t("puzzle.sliding.select-difficulty")}
                </Typography>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                        gap: 1,
                    }}
                >
                    {DIFF_LEVELS.map((level) => {
                        const entry = findEntry(list, selectedGridSize, level);
                        const template = entry?.template ?? null;
                        const levelLabel = t(`puzzle.card.difficulty-${level.toLowerCase()}`);
                        const chipColor = LEVEL_COLORS[level] || "default";
                        const isSelected =
                            Boolean(template && selectedTemplate?.id === template.id);
                        const rowLockReason = getSlidingGridLockReason(
                            selectedGridSize,
                            accessContext,
                            level,
                            { isFreePuzzle },
                        );
                        const showStandardTierBadge =
                            !isFreePuzzle
                            && isSlidingPremiumTier(selectedGridSize, level)
                            && !accessContext?.userHasStandardPlan;
                        const isCompleted = isSlidingTemplateCompletedByUser(template);
                        const unavailable = !template;

                        return (
                            <Tooltip
                                key={level}
                                title={
                                    unavailable
                                        ? t("puzzle.sliding.size-not-linked", "Not available")
                                        : levelLabel
                                }
                            >
                                <Box sx={{ minWidth: 0 }}>
                                    <Button
                                        fullWidth
                                        variant={isSelected ? "contained" : "outlined"}
                                        color={chipColor}
                                        disabled={unavailable}
                                        onClick={() => handleLevelSelect(level)}
                                        sx={{
                                            minHeight: 52,
                                            px: 0.75,
                                            py: 0.75,
                                            textTransform: "none",
                                            flexDirection: "column",
                                            gap: 0.15,
                                            borderWidth: isSelected ? 2 : 1,
                                            opacity: unavailable ? 0.55 : 1,
                                            bgcolor: isSelected
                                                ? undefined
                                                : "rgba(255,255,255,0.06)",
                                            "&:hover": {
                                                bgcolor: isSelected
                                                    ? undefined
                                                    : "rgba(255,255,255,0.12)",
                                            },
                                        }}
                                    >
                                        <Typography
                                            component="span"
                                            variant="body2"
                                            sx={{
                                                fontWeight: 700,
                                                lineHeight: 1.2,
                                                color: isSelected ? "inherit" : LABEL_PRIMARY,
                                            }}
                                        >
                                            {levelLabel}
                                        </Typography>
                                        {isCompleted ? (
                                            <Typography
                                                component="span"
                                                variant="caption"
                                                sx={{
                                                    lineHeight: 1.2,
                                                    color: isSelected ? "inherit" : LABEL_MUTED,
                                                }}
                                            >
                                                {t("puzzle.card.completed", "Completed")}
                                            </Typography>
                                        ) : null}
                                        {(rowLockReason || showStandardTierBadge) && !unavailable ? (
                                            <Box
                                                sx={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: 0.35,
                                                    mt: 0.15,
                                                    color: isSelected ? "inherit" : LABEL_MUTED,
                                                }}
                                            >
                                                {showStandardTierBadge ? (
                                                    <StarIcon sx={{ fontSize: 14 }} aria-hidden />
                                                ) : null}
                                                {rowLockReason ? (
                                                    <Lock sx={{ fontSize: 14 }} aria-hidden />
                                                ) : null}
                                            </Box>
                                        ) : null}
                                    </Button>
                                </Box>
                            </Tooltip>
                        );
                    })}
                </Box>
            </Box>

            {!isFreePuzzle
                && selectedTemplate
                && isSlidingPremiumTier(selectedGridSize, selectedLevel)
                && !accessContext?.userHasStandardPlan ? (
                <Chip
                    icon={<StarIcon />}
                    label={t("puzzle.card.premium")}
                    size="small"
                    color="warning"
                    variant="filled"
                    sx={{ ...tierBadgeChipSx, alignSelf: "flex-start" }}
                />
            ) : null}

            <Box sx={{ pt: 0.5, display: "flex", justifyContent: "center" }}>
                {!selectedTemplate ? (
                    <Button
                        variant="contained"
                        disabled
                        sx={{ ...playContainedButtonSx, minHeight: 44, minWidth: 160, px: 4 }}
                    >
                        {t("puzzle.card.play")}
                    </Button>
                ) : lockReason === "upgrade" ? (
                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<Lock />}
                        onClick={() => onUpgrade?.()}
                        sx={{ minHeight: 44, minWidth: 160, px: 4 }}
                    >
                        {t("puzzle.card.upgrade-to-play")}
                    </Button>
                ) : lockReason === "login" ? (
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<Lock />}
                        onClick={() => {
                            if (storyId) {
                                navigate("/login", {
                                    state: { from: { pathname: `/puzzle/play-sliding/${storyId}` } },
                                });
                            }
                        }}
                        sx={{ minHeight: 44, minWidth: 160, px: 4 }}
                    >
                        {t("puzzle.card.sign-in-to-play")}
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        onClick={handlePlay}
                        sx={{ ...playContainedButtonSx, minHeight: 44, minWidth: 160, px: 4 }}
                    >
                        {t("puzzle.card.play")}
                    </Button>
                )}
            </Box>
        </Stack>
    );
}
